import { ReactNode, useState } from 'react';
import { Chevron } from 'assets/svgs';
import { Lock, Unlock } from 'assets/svgs/16px';
import LoadingContent from 'components/LoadingContent';
import UI from 'constants/ui';

import {
  AccordionContent,
  AccordionHeader,
  AccordionHeaderLeft,
  AccordionWrapper,
  ChevronWrapper,
} from './styles';

interface AccordionProps {
  header: ReactNode;
  initialOpen?: boolean;
  loading?: boolean;

  /**
   * The content that is inside the accordion.
   * If the content is heavily stateful, consider using `React.lazy`.
   */
  children: ReactNode;

  disabled?: boolean;

  /**
   * Allow the padding of the header to be customised, some accordions may have more padding than others
   */
  headerPadding?: string;

  /**
   * Allow the padding of the content to be customised, some accordions may have more padding than others
   */
  padding?: string;

  /**
   * Optionally, the parent can control the open state
   */
  open?: boolean;

  /** Optionally, display lock/unlock icon on right side of header */
  showLock?: boolean;
  isLocked?: boolean;

  toggleOpen?: (open: boolean) => void;
}

const Accordion = (props: AccordionProps) => {
  const {
    header,
    initialOpen,
    disabled,
    children,
    toggleOpen,
    headerPadding = `${UI.spacing.lg} 0`,
    padding,
    loading = false,
    showLock = false,
    isLocked = false,
  } = props;

  // Note that `open` controls `display: hidden` rather than a conditional render.
  // This ensures that the content is not un-rendered and maintains state.
  const [internalOpen, setInternalOpen] = useState(initialOpen ?? false);

  const open = props.open ?? internalOpen;
  const setOpen = (open: boolean) => {
    return toggleOpen ? toggleOpen(open) : setInternalOpen(open);
  };

  return (
    <AccordionWrapper $padding={padding}>
      <AccordionHeader
        onClick={() => (disabled ? null : setOpen(!open))}
        $padding={headerPadding}
        $disabled={disabled}
        $canClick={!disabled}
      >
        <AccordionHeaderLeft>{header}</AccordionHeaderLeft>
        {showLock && <>{isLocked ? <Lock /> : <Unlock />}</>}
        <ChevronWrapper open={open}>
          <Chevron direction="down" />
        </ChevronWrapper>
      </AccordionHeader>

      <AccordionContent open={open && loading}>
        <LoadingContent />
      </AccordionContent>
      <AccordionContent open={open && !loading}>{children}</AccordionContent>
    </AccordionWrapper>
  );
};

export default Accordion;
